import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, retry, tap } from 'rxjs/operators';
import {
  CaasRoutes,
  CloudNavigationResponse,
} from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { MenuItem } from '../../../hdi-components/src/shared/interfaces/caas/nav.interface';
import { environment } from '../environments/environment';
import { GlobalServices } from './global.service';
import { isPlatformBrowser } from '@angular/common';
import { CaasContentService } from './caas-content.service';
import { Redirects } from '../../../hdi-components/src/shared/interfaces/redirects.interface';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly isPreview = environment.preview ? '@preview' : '';
  private cache: CaasRoutes[] | null = null;
  // 642697 23.12.2025 lr removed meta navigation
  private readonly specialRoutes = [
    'startseite',
    '404',
    '500',
    //    'metanavigation',
    //    'meta-navigation',
    'footer',
  ];

  private readonly blacklistKeywords = ['w1', 'lux'];

  constructor(
    private readonly http: HttpClient,
    private readonly globalServices: GlobalServices,
    private readonly transferState: TransferState,
    private readonly caasContentService: CaasContentService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  getCloudNavigation(): Observable<CaasRoutes[]> {
    const DATA_KEY_NAVIGATION = makeStateKey<CaasRoutes[]>('navigation');
    const cloudUrl = this.buildCloudUrl();

    if (isPlatformBrowser(this.platformId)) {
      const serverNavigation = this.transferState.get(
        DATA_KEY_NAVIGATION,
        null
      );
      if (serverNavigation) {
        return of(serverNavigation);
      }
    }
    if (this.cache) {
      return of(this.cache);
    }

    // Set this value to true to simulate the 500 error
    const simulateError = false;

    return this.http.get<CloudNavigationResponse>(cloudUrl).pipe(
      retry(3),
      mergeMap((data: CloudNavigationResponse) => {
        // Logic for error simulation
        if (simulateError) {
          console.log('Simulating 500 Internal Server Error...');
          return throwError(
            () =>
              new HttpErrorResponse({
                status: 500,
                statusText: 'Internal Server Error Navigation (Simulated)',
              })
          );
        }

        if (!data.children || data.children.length === 0) {
          // Return a successful but empty Observable
          return of([]);
        }

        for (let i = 0; i < data.children.length; i++) {
          console.log('Label:', data.children[i].label);
          if (data.children[i].label === 'navigation') {
            const caasRoutesArray: CaasRoutes[] = data.children[i].children.map(
              (child) => this.mapCloudNavigationToCaasRoutes(child)
            );

            // On the server: store data for the client
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_NAVIGATION, caasRoutesArray);
            }

            return of(caasRoutesArray || []);
          }
        }
        console.log('No element with the label <Navigation> found.');
        return of([]);
      }),
      tap((data: CaasRoutes[]) => {
        // This part is only executed on success
        this.cache = data;
      }),
      catchError((error) => {
        return this.globalServices.handleError(
          error,
          'NavigationService > getCloudNavigation',
          true // redirect500 only for 5xx errors
        );
      })
    );
  }

  private mapCloudNavigationToCaasRoutes(
    response: CloudNavigationResponse
  ): CaasRoutes {
    return {
      children: response.hasChildren
        ? response.children.map((child) =>
            this.mapCloudNavigationToCaasRoutes(child)
          )
        : [],
      identifier: response.contentReference
        ? (response.contentReference
            .split('/')
            .pop()
            ?.replace(/\.de_DE$/, '') ?? '')
        : '',
      label: response.label,
      seoRoute: response.seoRoute
        ? response.seoRoute
            .replace(/^\/navigation/i, '')
            .replace(/\.html$/i, '')
            .replace(/(meta-navigation\/)$/i, (match) => {
              // If nothing comes after meta-navigation/, append the uid
              const uid = response.customData?.uid ?? '';
              return uid ? `meta-navigation/${uid}` : match;
            })
        : '',
      uid:
        response.children?.find((child) => child.label === response.label)
          ?.customData?.uid ??
        (response.customData ? response.customData.uid : ''),
      showInNavigation:
        response.customData && 'showInNavigation' in response.customData
          ? response.customData.showInNavigation
          : true,
    };
  }

  private capitalize(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  /**
   * Generates breadcrumbs based on the navigation hierarchy and current path.
   * @param seoRoute The current route for which to generate breadcrumbs.
   * @returns An array of breadcrumb items.
   */
  generateBreadcrumbs(
    seoRoute: string,
    navigation: CaasRoutes[]
  ): { title: string; url: string }[] {
    const breadcrumbs: { title: string; url: string }[] = [];
    let currentPath = seoRoute.toLowerCase().replace(/(^\/|\/$)/g, '');

    // Helper function to recursively find the path in the navigation tree
    const findPath = (
      routes: CaasRoutes[],
      path: string
    ): CaasRoutes | null => {
      for (const route of routes) {
        const simplifiedPath = route.seoRoute
          .toLowerCase()
          .replace(/(^\/|\/$)/g, '');
        if (simplifiedPath === path) {
          return route;
        }
        if (route.children && route.children.length > 0) {
          const result = findPath(route.children, path);
          if (result) {
            return result;
          }
        }
      }
      return null;
    };

    // Recursively generate breadcrumbs
    while (currentPath) {
      const matchingRoute = findPath(navigation, currentPath);
      if (matchingRoute) {
        breadcrumbs.unshift({
          title: matchingRoute.label || this.capitalize(currentPath),
          url: `/${currentPath}`,
        });
        currentPath = currentPath.split('/').slice(0, -1).join('/');
      } else {
        breadcrumbs.unshift({
          title: this.capitalize(
            seoRoute
              .trim()
              .toLowerCase()
              .replace(/(^\/|\/$)/g, '')
          ),
          url: seoRoute,
        });
        break;
      }
    }

    breadcrumbs.unshift({ title: 'Startseite', url: '/startseite' });
    return breadcrumbs;
  }

  /**
   * Retrieve the seoRoute based on the pageIdentifier.
   * @param pageIdentifier The identifier to look up.
   * @param navigation The navigation data to search through.
   * @returns The corresponding seoRoute.
   */
  getSeoRouteByIdentifier(
    pageIdentifier: string,
    navigation: CaasRoutes[]
  ): string | null {
    const findSeoRoute = (routes: CaasRoutes[], id: string): string | null => {
      for (const route of routes) {
        if (route.identifier === id) {
          return route.seoRoute;
        }

        // Recursively check children routes
        if (route.children && route.children.length > 0) {
          const found = findSeoRoute(route.children, id);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    return findSeoRoute(navigation, pageIdentifier);
  }

  getRedirects(): Observable<Redirects[]> {
    return this.caasContentService.getRedirects();

    /*        return this.caasContentService.getRedirects().pipe(
      tap((result) => {
        console.debug('URL-Redirects:', result);
      })
    );*/
  }

  /**
   * Transforms the navigation data into a format suitable for menu display.
   * @returns An observable of the main menu items.
   */
  getMainNavigation(): Observable<MenuItem[]> {
    return this.getCloudNavigation().pipe(
      map((data) => {
        data = this.extractMainNavigation(data);
        return this.transformToMenuItems(data);
      })
    );
  }

  // 642697 23.12.2025 lr removed meta navigation
  /*  getMetaRoutes(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['metanavigation', 'meta-navigation'];
        const filteredMetaRoutes = this.filterKeywordsRoutes(data, keywords);
        return filteredMetaRoutes;
      })
    );
  }*/

  getFooterRoutes(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['footer'];
        const filteredFooterRoutes = this.filterKeywordsRoutes(data, keywords);
        return filteredFooterRoutes;
      })
    );
  }

  getNonSpecialRoutes(): Observable<CaasRoutes[]> {
    return this.getCloudNavigation().pipe(
      map((routes) => routes.filter((route) => !this.isSpecialRoute(route)))
    );
  }

  private isSpecialRoute(route: CaasRoutes): boolean {
    return this.specialRoutes.some(
      (keyword) =>
        route.seoRoute.toLowerCase().includes(keyword) ||
        route.uid.toLowerCase().includes(keyword)
    );
  }

  getSpecialRoutes(): Observable<CaasRoutes[]> {
    return this.getCloudNavigation().pipe(
      map((data: CaasRoutes[]) => {
        return this.filterKeywordsRoutes(data, this.specialRoutes) || [];
      })
    );
  }

  get404(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['404'];
        return this.filterKeywordsRoutes(data, keywords);
      })
    );
  }

  get500(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['500'];
        return this.filterKeywordsRoutes(data, keywords);
      })
    );
  }

  getHomePage(): Observable<CaasRoutes[]> {
    return this.getSpecialRoutes().pipe(
      map((data: CaasRoutes[]) => {
        const keywords = ['startseite', 'home'];
        return this.filterKeywordsRoutes(data, keywords);
      })
    );
  }

  getFlyoutData(): Observable<
    { title: string; url: string; subpages: { title: string; url: string }[] }[]
  > {
    if (this.cache) {
      return of(this.buildFlyoutData(this.cache));
    }
    return this.getCloudNavigation().pipe(
      map((data: CaasRoutes[]) => {
        const flyoutData = this.buildFlyoutData(data);
        return flyoutData;
      })
    );
  }

  private buildFlyoutData(items: CaasRoutes[]): {
    title: string;
    url: string;
    subpages: { title: string; url: string }[];
  }[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const filteredItems = items.filter((item) => item.children?.length);

    return filteredItems
      .map((item) => {
        const parentUrl = this.simplifyPath(
          item.seoRoute.toLowerCase().replace(/(^\/|\/$)/g, '')
        );

        const subpages = item.children
          .map((child) => {
            const childUrl = this.simplifyPath(
              child.seoRoute.toLowerCase().replace(/(^\/|\/$)/g, '')
            );
            return {
              title: child.label,
              url: childUrl,
            };
          })
          .filter(
            (subItem) =>
              subItem.title && subItem.url && subItem.url !== parentUrl
          );

        return {
          title: item.label,
          url: parentUrl,
          subpages,
        };
      })
      .filter((data) => {
        if (data.subpages.length === 0) {
          return false;
        }
        return true;
      });
  }

  private filterKeywordsRoutes(
    data: CaasRoutes[],
    keywords: string[]
  ): CaasRoutes[] {
    const regexPattern = new RegExp(keywords.join('|'), 'i');

    return data.filter((item) => {
      const seoRouteTrimmed = item.seoRoute.trim();
      const uidTrimmed = item.uid.trim();

      const matchesKeyword =
        regexPattern.test(seoRouteTrimmed) || regexPattern.test(uidTrimmed);

      return matchesKeyword;
    });
  }

  private simplifyPath(path: string) {
    const segments: string[] = path.split('/');
    const result: string[] = [];

    for (let i = 0; i < segments.length; i++) {
      if (i === 0 || segments[i] !== segments[i - 1]) {
        result.push(segments[i]);
      }
    }

    return result.join('/');
  }

  private buildCloudUrl(): string {
    return `${environment.cloudNavigationUrl}`;
  }

  private extractMainNavigation(data: CaasRoutes[]): CaasRoutes[] {
    if (!data || data.length === 0) {
      return [];
    }

    const mainNavigation = data.filter((item) => {
      const isBlacklisted =
        this.filterKeywordsRoutes([item], this.blacklistKeywords).length > 0;

      return (
        item.children &&
        item.children.length > 0 &&
        item.showInNavigation &&
        !this.isSpecialRoute(item) &&
        !isBlacklisted
      );
    });

    return mainNavigation;
  }

  private transformToMenuItems(data: CaasRoutes[]): MenuItem[] {
    return data
      .filter((item) => {
        return !item.label.toLowerCase().includes('startseite');
      })
      .map((item) => {
        return {
          title: item.label,
          url: this.simplifyPath(
            item.seoRoute.toLowerCase().replace(/(^\/|\/$)/g, '')
          ),
          subitems: item.children
            .map((subItem) => ({
              title: subItem.label,
              url: this.simplifyPath(
                subItem.seoRoute.toLowerCase().replace(/(^\/|\/$)/g, '')
              ),
              subitems: subItem.children.map((subSubItem) => ({
                title: subSubItem.label,
                url: this.simplifyPath(
                  subSubItem.seoRoute.toLowerCase().replace(/(^\/|\/$)/g, '')
                ),
              })),
            }))
            .filter((subItem) => subItem.title !== item.label),
        };
      });
  }
}
